import * as THREE from "three";
import { FBXLoader } from "three/addons/loaders/FBXLoader.js";
import type { Face, MaterialData, MeshData, SceneDocument, Vec2, Vec3 } from "../types";
import { refreshEdgeIds } from "../core/mesh";

const encoder = new TextEncoder();

function n(value: number): string {
  if (!Number.isFinite(value)) return "0";
  const normalized = Math.abs(value) < 1e-10 ? 0 : value;
  return Number(normalized.toFixed(7)).toString();
}

function arrayBlock(name: string, values: Array<number | string>, indent = "\t\t"): string {
  return `${indent}${name}: *${values.length} {\n${indent}\ta: ${values.join(",")}\n${indent}}\n`;
}

function faceNormal(mesh: MeshData, face: Face): Vec3 {
  const p = face.vertices.map((id) => mesh.vertices.find((vertex) => vertex.id === id)!.position);
  const a = new THREE.Vector3(...p[0]);
  const b = new THREE.Vector3(...p[1]);
  const c = new THREE.Vector3(...p[2]);
  return b.sub(a).cross(c.sub(a)).normalize().toArray() as Vec3;
}

export function exportFbxAscii(scene: SceneDocument): Uint8Array {
  const chunks: string[] = [];
  const geometryIds: number[] = [];
  const modelIds: number[] = [];
  const materialIds = new Map<string, number>();
  let id = 100000;
  scene.materials.forEach((material) => materialIds.set(material.id, id++));
  scene.objects.forEach(() => { geometryIds.push(id++); modelIds.push(id++); });

  chunks.push(`; FBX 7.4.0 project file\n; Created by Feral3D\nFBXHeaderExtension:  {\n\tFBXHeaderVersion: 1003\n\tFBXVersion: 7400\n\tCreator: "Feral3D 0.1.0"\n}\nGlobalSettings:  {\n\tVersion: 1000\n\tProperties70:  {\n\t\tP: "UpAxis", "int", "Integer", "",1\n\t\tP: "UpAxisSign", "int", "Integer", "",1\n\t\tP: "FrontAxis", "int", "Integer", "",2\n\t\tP: "FrontAxisSign", "int", "Integer", "",-1\n\t\tP: "CoordAxis", "int", "Integer", "",0\n\t\tP: "CoordAxisSign", "int", "Integer", "",1\n\t\tP: "UnitScaleFactor", "double", "Number", "",1\n\t\tP: "OriginalUnitScaleFactor", "double", "Number", "",1\n\t}\n}\nDocuments:  {\n\tCount: 1\n\tDocument: 1, "Scene", "Scene" {\n\t\tProperties70:  {\n\t\t\tP: "SourceObject", "object", "", ""\n\t\t\tP: "ActiveAnimStackName", "KString", "", "", ""\n\t\t}\n\t\tRootNode: 0\n\t}\n}\nReferences:  {\n}\nDefinitions:  {\n\tVersion: 100\n\tCount: ${scene.objects.length * 2 + scene.materials.length + 1}\n\tObjectType: "GlobalSettings" {\n\t\tCount: 1\n\t}\n\tObjectType: "Model" {\n\t\tCount: ${scene.objects.length}\n\t}\n\tObjectType: "Geometry" {\n\t\tCount: ${scene.objects.length}\n\t}\n\tObjectType: "Material" {\n\t\tCount: ${scene.materials.length}\n\t}\n}\nObjects:  {\n`);

  scene.materials.forEach((material) => {
    const materialId = materialIds.get(material.id)!;
    const color = new THREE.Color(material.color);
    chunks.push(`\tMaterial: ${materialId}, "Material::${material.name}", "" {\n\t\tVersion: 102\n\t\tShadingModel: "phong"\n\t\tMultiLayer: 0\n\t\tProperties70:  {\n\t\t\tP: "DiffuseColor", "Color", "", "A",${n(color.r)},${n(color.g)},${n(color.b)}\n\t\t\tP: "DiffuseFactor", "Number", "", "A",1\n\t\t\tP: "Shininess", "double", "Number", "",${n((1 - material.roughness) * 100)}\n\t\t}\n\t}\n`);
  });

  scene.objects.forEach((mesh, objectIndex) => {
    const geometryId = geometryIds[objectIndex];
    const modelId = modelIds[objectIndex];
    const vertexIndex = new Map(mesh.vertices.map((vertex, index) => [vertex.id, index]));
    const vertices = mesh.vertices.flatMap((vertex) => vertex.position.map(n));
    const polygonIndices: number[] = [];
    const normals: string[] = [];
    const uvs: string[] = [];
    const uvIndices: number[] = [];
    const materialIndices: number[] = [];
    const materialOrder = [...new Set(mesh.faces.map((face) => face.materialId))];
    let uvIndex = 0;
    for (const face of mesh.faces) {
      const normal = faceNormal(mesh, face);
      face.vertices.forEach((vertexId, index) => {
        const mapped = vertexIndex.get(vertexId)!;
        polygonIndices.push(index === face.vertices.length - 1 ? -mapped - 1 : mapped);
        normals.push(n(normal[0]), n(normal[1]), n(normal[2]));
        const uv = face.uvs[index] ?? [0, 0];
        uvs.push(n(uv[0]), n(uv[1]));
        uvIndices.push(uvIndex++);
      });
      materialIndices.push(Math.max(0, materialOrder.indexOf(face.materialId)));
    }
    chunks.push(`\tGeometry: ${geometryId}, "Geometry::${mesh.name}", "Mesh" {\n\t\tGeometryVersion: 124\n`);
    chunks.push(arrayBlock("Vertices", vertices));
    chunks.push(arrayBlock("PolygonVertexIndex", polygonIndices));
    chunks.push(`\t\tLayerElementNormal: 0 {\n\t\t\tVersion: 101\n\t\t\tName: ""\n\t\t\tMappingInformationType: "ByPolygonVertex"\n\t\t\tReferenceInformationType: "Direct"\n${arrayBlock("Normals", normals, "\t\t\t")}\t\t}\n`);
    chunks.push(`\t\tLayerElementUV: 0 {\n\t\t\tVersion: 101\n\t\t\tName: "UVChannel_1"\n\t\t\tMappingInformationType: "ByPolygonVertex"\n\t\t\tReferenceInformationType: "IndexToDirect"\n${arrayBlock("UV", uvs, "\t\t\t")}${arrayBlock("UVIndex", uvIndices, "\t\t\t")}\t\t}\n`);
    chunks.push(`\t\tLayerElementMaterial: 0 {\n\t\t\tVersion: 101\n\t\t\tName: ""\n\t\t\tMappingInformationType: "ByPolygon"\n\t\t\tReferenceInformationType: "IndexToDirect"\n${arrayBlock("Materials", materialIndices, "\t\t\t")}\t\t}\n`);
    chunks.push(`\t\tLayer: 0 {\n\t\t\tVersion: 100\n\t\t\tLayerElement:  {\n\t\t\t\tType: "LayerElementNormal"\n\t\t\t\tTypedIndex: 0\n\t\t\t}\n\t\t\tLayerElement:  {\n\t\t\t\tType: "LayerElementMaterial"\n\t\t\t\tTypedIndex: 0\n\t\t\t}\n\t\t\tLayerElement:  {\n\t\t\t\tType: "LayerElementUV"\n\t\t\t\tTypedIndex: 0\n\t\t\t}\n\t\t}\n\t}\n`);
    const transform = mesh.transform;
    chunks.push(`\tModel: ${modelId}, "Model::${mesh.name}", "Mesh" {\n\t\tVersion: 232\n\t\tProperties70:  {\n\t\t\tP: "Lcl Translation", "Lcl Translation", "", "A",${transform.position.map(n).join(",")}\n\t\t\tP: "Lcl Rotation", "Lcl Rotation", "", "A",${transform.rotation.map((value) => n(value * 180 / Math.PI)).join(",")}\n\t\t\tP: "Lcl Scaling", "Lcl Scaling", "", "A",${transform.scale.map(n).join(",")}\n\t\t}\n\t\tShading: T\n\t\tCulling: "CullingOff"\n\t}\n`);
  });
  chunks.push("}\nConnections:  {\n");
  scene.objects.forEach((mesh, objectIndex) => {
    chunks.push(`\tC: "OO",${geometryIds[objectIndex]},${modelIds[objectIndex]}\n`);
    chunks.push(`\tC: "OO",${modelIds[objectIndex]},0\n`);
    const materialOrder = [...new Set(mesh.faces.map((face) => face.materialId))];
    materialOrder.forEach((materialId) => {
      const fbxId = materialIds.get(materialId);
      if (fbxId) chunks.push(`\tC: "OO",${fbxId},${modelIds[objectIndex]}\n`);
    });
  });
  chunks.push("}\nTakes:  {\n\tCurrent: \"\"\n}\n");
  return encoder.encode(chunks.join(""));
}

function materialFromThree(material: THREE.Material | THREE.Material[]): MaterialData {
  const first = Array.isArray(material) ? material[0] : material;
  const standard = first as THREE.MeshStandardMaterial;
  return {
    id: crypto.randomUUID(),
    name: first?.name || "Imported Material",
    color: standard?.color ? `#${standard.color.getHexString()}` : "#8fa39a",
    roughness: standard?.roughness ?? 0.7,
    metallic: standard?.metalness ?? 0,
  };
}

export async function importFbx(bytes: ArrayBuffer, filename = "Imported FBX"): Promise<{ meshes: MeshData[]; materials: MaterialData[]; warnings: string[] }> {
  const loader = new FBXLoader();
  const group = loader.parse(bytes, "");
  const meshes: MeshData[] = [];
  const materials: MaterialData[] = [];
  const warnings = ["FBX animation, skeletons, skin weights, and blend shapes are ignored. Imported FBX polygons are triangulated by the browser loader."];
  group.updateMatrixWorld(true);
  group.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    const geometry = object.geometry.toNonIndexed();
    const position = geometry.getAttribute("position");
    const uv = geometry.getAttribute("uv");
    const material = materialFromThree(object.material);
    materials.push(material);
    const vertices: MeshData["vertices"] = [];
    const faces: MeshData["faces"] = [];
    let nextVertexId = 1;
    let nextFaceId = 1;
    for (let index = 0; index < position.count; index += 3) {
      const ids: number[] = [];
      const faceUvs: Vec2[] = [];
      for (let corner = 0; corner < 3; corner += 1) {
        const offset = index + corner;
        const vector = new THREE.Vector3(position.getX(offset), position.getY(offset), position.getZ(offset));
        vector.applyMatrix4(object.matrixWorld);
        ids.push(nextVertexId);
        vertices.push({ id: nextVertexId++, position: vector.toArray() as Vec3 });
        faceUvs.push(uv ? [uv.getX(offset), uv.getY(offset)] : [0, 0]);
      }
      faces.push({ id: nextFaceId++, vertices: ids, uvs: faceUvs, materialId: material.id });
    }
    const mesh: MeshData = {
      id: crypto.randomUUID(),
      name: object.name || filename.replace(/\.fbx$/i, ""),
      vertices,
      faces,
      edgeIds: {},
      nextVertexId,
      nextFaceId,
      nextEdgeId: 1,
      transform: { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1], pivot: [0, 0, 0] },
      visible: true,
    };
    meshes.push(refreshEdgeIds(mesh));
  });
  if (!meshes.length) throw new Error("The FBX did not contain a static polygon mesh.");
  return { meshes, materials, warnings };
}
